'use client';

import { useMemo } from 'react';

import { Heading, Stack } from '@backyard-ui/core';

import { type ItemId } from '@/infra/services/catalog/model';
import { useSummary } from '@/presenters/hooks/catalog/use-summary';
import { useDefaultStore } from '@/presenters/store/catalog/default';

import { EmptyFeedback } from './EmptyFeedback';
import { HelpfulReviews } from './HelpfulReviews';
import { Resume } from './Resume';
import { Reviews } from './Reviews';
import { Skeleton } from './Skeleton';

interface ReviewsSectionProps {
  itemId: ItemId;
}

function ReviewsSection(props: Readonly<ReviewsSectionProps>) {
  const { itemId } = props;

  const initialSummary = useDefaultStore((store) => store.review.summary);

  const {
    data: summary,
    isFetching,
    isError,
  } = useSummary(itemId, {
    initialData: initialSummary && { data: initialSummary },
  });

  const content = useMemo(() => {
    if (isFetching) {
      return <Skeleton />;
    }

    if (!summary?.data.generalScores || isError) {
      return <EmptyFeedback />;
    }

    return (
      <>
        <Resume summary={summary.data} />
        <HelpfulReviews summary={summary.data} />
        <Reviews itemId={itemId} />
      </>
    );
  }, [summary, isError, isFetching, itemId]);

  return (
    <Stack direction="column" spacing="16" className="scroll-mt-12">
      <Heading size="2xl" asChild>
        <h2>Avaliações</h2>
      </Heading>
      {content}
    </Stack>
  );
}

export default ReviewsSection;
